import { makeAutoObservable } from "mobx"
import { RouterStore } from "mobx-router"
import { translations } from "../i18n/translations"
import { logger } from "../lib/logger"
import { AuthStore } from "./AuthStore"
import { CustomDashboardStore } from "./CustomDashboardStore"
import { DataStore } from "./DataStore"
import { DoraMetricsStore } from "./DoraMetricsStore"
import { I18nStore } from "./I18nStore"
import { MetadataStore } from "./MetadataStore"
import { SettingsStore } from "./SettingsStore"
import { ThemeStore } from "./ThemeStore"
import { ViewStore } from "./ViewStore"

export class RootStore {
  router: RouterStore<RootStore>
  auth: AuthStore
  theme: ThemeStore
  i18n: I18nStore
  data: DataStore
  metadata: MetadataStore
  view: ViewStore
  doraMetrics: DoraMetricsStore
  settings: SettingsStore
  customDashboard: CustomDashboardStore

  // Track if data has been initialized
  private dataInitialized = false

  constructor() {
    makeAutoObservable(this)
    this.router = new RouterStore<RootStore>(this)
    this.auth = new AuthStore()
    this.theme = new ThemeStore()
    this.i18n = new I18nStore(translations)
    this.data = new DataStore()
    this.metadata = new MetadataStore()
    this.view = new ViewStore()
    this.doraMetrics = new DoraMetricsStore()
    this.settings = new SettingsStore()
    this.customDashboard = new CustomDashboardStore()
  }

  /**
   * Initialize data stores
   * This should be called once after auth is complete (or immediately if no auth)
   */
  async initializeData(): Promise<void> {
    if (this.dataInitialized) {
      logger.debug("Data already initialized, skipping", {
        component: "RootStore",
      })
      return
    }

    try {
      logger.info("Initializing data stores...", { component: "RootStore" })

      // Load metadata and data in parallel
      await Promise.all([
        this.metadata.initialize(),
        this.data.initialize(),
        this.doraMetrics.initialize(),
      ])

      // Initialize teams in settings store after metadata is loaded
      this.settings.initializeTeams(this.metadata.uniqueTeams)

      // Initialize custom dashboard store
      this.customDashboard.initialize()

      this.dataInitialized = true
      logger.info("Data stores initialized successfully", {
        component: "RootStore",
      })
    } catch (error) {
      logger.error("Failed to initialize data stores", error, {
        component: "RootStore",
      })
      throw error
    }
  }

  /**
   * Check if data is initialized and ready
   */
  get isDataReady(): boolean {
    return (
      this.dataInitialized &&
      !this.data.isLoading &&
      !this.metadata.isLoading &&
      !this.doraMetrics.isLoading
    )
  }

  /**
   * Reset all stores
   */
  reset(): void {
    this.data.reset()
    this.metadata.reset()
    this.view.reset()
    // Note: doraMetrics doesn't have a reset method yet
    this.dataInitialized = false
    logger.info("RootStore reset", { component: "RootStore" })
  }
}

export const rootStore = new RootStore()
